/**
 * Asset Helpers - Country-Agnostic Asset Access
 *
 * Provides utilities for accessing resident country assets from the store.
 * All assets are now stored in residentCountryAssets (no legacy usaAssets).
 */

import type { CalculatorState, USAAssets, StateTaxTier, RSUGrant, StockOption, OtherAsset } from '@/types'

/**
 * Build USAAssets object from residentCountryAssets
 *
 * Transforms the country-agnostic residentCountryAssets into USAAssets format
 * for backward compatibility with existing calculators that expect USAAssets shape.
 *
 * @param state - The calculator state
 * @returns USAAssets object built from residentCountryAssets
 */
export function buildUSAAssetsFromResident(state: CalculatorState): USAAssets {
  const { residentCountryAssets, profile } = state

  // If user is not USA-based, return empty USAAssets
  if (profile.residentCountry !== 'USA') {
    return getEmptyUSAAssets()
  }

  const assets = residentCountryAssets?.assets || {}
  const complexAssets = residentCountryAssets?.complexAssets || {}
  const settings = residentCountryAssets?.settings || {}

  return {
    // Retirement accounts
    balance401k: assets.balance401k || 0,
    employer401kName: '',
    balanceRothIRA: assets.balanceRothIRA || 0,
    balanceTraditionalIRA: assets.balanceTraditionalIRA || 0,
    balanceHSA: assets.balanceHSA || 0,

    // Investment accounts
    taxableBrokerage: assets.taxableBrokerage || 0,
    brokerageCostBasis: assets.brokerageCostBasis || 0,
    savingsChecking: assets.savingsChecking || 0,
    balance529Plan: assets.balance529Plan || 0,
    crypto: assets.crypto || 0,

    // Employer equity
    rsuEquityValue: assets.rsuEquityValue || 0,
    esppBalance: assets.esppBalance || 0,
    rsuGrants: complexAssets.rsuGrants || [],
    stockOptions: complexAssets.stockOptions || [],

    // Real estate
    primaryHomeValue: assets.primaryHomeValue || 0,
    primaryHomeMortgage: assets.primaryHomeMortgage || 0,
    primaryHomeRentalIncome: assets.primaryHomeRentalIncome || 0,
    rentalPropertyValue: assets.rentalPropertyValue || 0,
    rentalPropertyMortgage: assets.rentalPropertyMortgage || 0,
    rentalPropertyIncome: assets.rentalPropertyIncome || 0,

    // Social Security
    // Eligibility requires 10+ years of work in USA (40 credits)
    socialSecurityMonthlyBenefit: assets.socialSecurityMonthlyBenefit || 0,
    socialSecurityStartAge: assets.socialSecurityStartAge || 67,
    socialSecurityEligible: (profile.yearsWorkedInUSA ?? 0) >= 10,

    // Tax settings
    stateTaxTier: settings.stateTaxTier || 'none',

    // Other assets
    otherAssets: complexAssets.otherAssets || [],
  }
}

/**
 * Get empty USAAssets for non-USA users
 */
export function getEmptyUSAAssets(): USAAssets {
  return {
    balance401k: 0,
    employer401kName: '',
    balanceRothIRA: 0,
    balanceTraditionalIRA: 0,
    balanceHSA: 0,
    taxableBrokerage: 0,
    brokerageCostBasis: 0,
    savingsChecking: 0,
    balance529Plan: 0,
    crypto: 0,
    rsuEquityValue: 0,
    esppBalance: 0,
    rsuGrants: [],
    stockOptions: [],
    primaryHomeValue: 0,
    primaryHomeMortgage: 0,
    primaryHomeRentalIncome: 0,
    rentalPropertyValue: 0,
    rentalPropertyMortgage: 0,
    rentalPropertyIncome: 0,
    socialSecurityMonthlyBenefit: 0,
    socialSecurityStartAge: 67,
    socialSecurityEligible: false,
    stateTaxTier: 'none',
    otherAssets: [],
  }
}

/**
 * Check if the user has any resident country assets
 */
export function hasResidentAssets(state: CalculatorState): boolean {
  const assets = state.residentCountryAssets?.assets || {}
  const complexAssets = state.residentCountryAssets?.complexAssets || {}

  const hasSimpleAssets = Object.values(assets).some(v => v > 0)
  const hasRSUs = (complexAssets.rsuGrants || []).length > 0
  const hasOptions = (complexAssets.stockOptions || []).length > 0
  const hasOther = (complexAssets.otherAssets || []).length > 0

  return hasSimpleAssets || hasRSUs || hasOptions || hasOther
}

/**
 * Calculate total resident country liquid assets (excludes real estate)
 */
export function getTotalResidentLiquidAssets(state: CalculatorState): number {
  const assets = state.residentCountryAssets?.assets || {}
  const complexAssets = state.residentCountryAssets?.complexAssets || {}

  const retirement =
    (assets.balance401k || 0) +
    (assets.balanceRothIRA || 0) +
    (assets.balanceTraditionalIRA || 0) +
    (assets.balanceHSA || 0) +
    (assets.npsBalance || 0) +
    (assets.gratuityBalance || 0)

  const rsus = (complexAssets.rsuGrants || []).reduce(
    (sum: number, grant: RSUGrant) => sum + grant.vestedShares * grant.currentPricePerShare,
    0
  )

  const options = (complexAssets.stockOptions || []).reduce((sum: number, option: StockOption) => {
    const intrinsicValue = Math.max(0, option.currentPrice - option.strikePrice) * option.vestedShares
    return sum + intrinsicValue
  }, 0)

  const investments =
    (assets.taxableBrokerage || 0) +
    (assets.crypto || 0) +
    (assets.esppBalance || 0) +
    (assets.balance529Plan || 0) +
    (assets.stocks || 0) +
    (assets.mutualFunds || 0)

  const cash =
    (assets.savingsChecking || 0) +
    (assets.checkingBalance || 0) +
    (assets.savingsBalance || 0)

  const other = (complexAssets.otherAssets || []).reduce(
    (sum: number, asset: OtherAsset) => sum + asset.value,
    0
  )

  return retirement + rsus + options + investments + cash + other
}

/**
 * Get RSU grants from residentCountryAssets
 */
export function getResidentRSUGrants(state: CalculatorState): RSUGrant[] {
  return state.residentCountryAssets?.complexAssets?.rsuGrants || []
}

/**
 * Get stock options from residentCountryAssets
 */
export function getResidentStockOptions(state: CalculatorState): StockOption[] {
  return state.residentCountryAssets?.complexAssets?.stockOptions || []
}

/**
 * Get state tax tier from residentCountryAssets (USA only)
 */
export function getResidentStateTaxTier(state: CalculatorState): StateTaxTier {
  if (state.profile.residentCountry !== 'USA') {
    return 'none'
  }
  return state.residentCountryAssets?.settings?.stateTaxTier || 'none'
}

/**
 * Get other assets from residentCountryAssets
 */
export function getResidentOtherAssets(state: CalculatorState): OtherAsset[] {
  return state.residentCountryAssets?.complexAssets?.otherAssets || []
}

// Alias for callers using this function name
export const getEffectiveUSAAssets = buildUSAAssetsFromResident
